import React from 'react';

import get from 'lodash/get';
import css from './ChildComplete.module.scss';

import AppDownloadLinks from '../AppDownloadLinks';
import { useToggles } from '../../../state/toggles';
import { RewardBadge } from '../../Widgets/RewardBadge';
import { useUser } from '../../../state/user';

interface Props {
  appOs: string;
  nextRegStep: string;
}

interface ScreenValues {
  imageUrl: string;
  subHeaderText: string;
  showImageAtTop: boolean;
  downloadAppText: string;
  headerText: string;
  showPromoBadge: boolean;
}

const ChildComplete = (props: Props) => {
  const { referralRewardValue, userIsTeen } = useUser();
  const { growthRegistrationFlowFields } = useToggles();
  const growthScreenVariables = growthRegistrationFlowFields?.variables;
  const reward = !Number.isNaN(Number(referralRewardValue))
    ? Number(referralRewardValue)
    : 0;

  const getScreenValues = (): ScreenValues => {
    const baseSubHeaderText =
      'Your parent has been invited to complete your Greenlight setup. Once your parent registers with Greenlight, you’ll be all set. ' +
      '\nWe’ve sent a link to your mobile phone to download the Greenlight app. ';
    const baseHeaderText = 'Great!';
    const baseImageUrl = '../../../static/child-complete.png';
    const baseDownloadText = 'Install the app now:';
    const utmSource = JSON.parse(
      localStorage.getItem('adsSuperProperties') || ''
    )?.utm_source;
    const screenValues: ScreenValues = {
      imageUrl: baseImageUrl,
      subHeaderText: baseSubHeaderText,
      showImageAtTop: false,
      downloadAppText: baseDownloadText,
      headerText: baseHeaderText,
      showPromoBadge: false
    } as ScreenValues;

    if (!referralRewardValue || utmSource !== 'extole' || reward === 0) {
      return screenValues;
    }

    if (userIsTeen) {
      screenValues.imageUrl =
        get(growthScreenVariables, 'end_screen_image_url') || baseImageUrl;
      screenValues.subHeaderText =
        get(growthScreenVariables, 'end_screen_subhead') || baseSubHeaderText;
      screenValues.showImageAtTop =
        get(growthScreenVariables, 'end_screen_image_on_top') || false;
      screenValues.downloadAppText =
        get(growthScreenVariables, 'end_screen_download_text') ||
        baseDownloadText;
      screenValues.headerText =
        get(growthScreenVariables, 'end_screen_image_on_top') || baseHeaderText;
      screenValues.showPromoBadge =
        get(growthScreenVariables, 'end_screen_show_referral_reward') || false;
    }

    return screenValues;
  };

  const {
    imageUrl,
    subHeaderText,
    showImageAtTop,
    downloadAppText,
    headerText,
    showPromoBadge
  } = getScreenValues();
  const pendingParentCompletion =
    props.nextRegStep === 'child_pending_parent_completion';
  const text = pendingParentCompletion
    ? subHeaderText
    : "You're all set! If you haven't already received your Greenlight card, it should arrive in approximately 7 - 10 business days.";
  const isInApp = !!props.appOs;
  const header = !pendingParentCompletion ? 'Great!' : headerText;
  const bodyText = text.split('\n').map(item => <p key={item}>{item}</p>);

  const showPromo = () => {
    if (showPromoBadge && reward > 0) {
      return (
        <div className={css.badge}>
          <RewardBadge head={'Get'} value={reward} />
        </div>
      );
    }
  };
  const getBody = () => {
    let body;
    if (showImageAtTop) {
      body = (
        <>
          <div className={css.CompleteImageAtTopContainer}>
            <img
              className={css.CompleteImageAtTop}
              src={imageUrl}
              alt="Greenlight card app"
            />
            {showPromo()}
          </div>
          <h1 className={css.header}>{header}</h1>
        </>
      );
    } else {
      body = (
        <>
          <h1 className={css.header}>{header}</h1>
          <div className={css.CompleteImage}>
            <img src={imageUrl} alt="Greenlight card app" />
          </div>
        </>
      );
    }
    return body;
  };

  return isInApp ? (
    <div />
  ) : (
    <div
      className={css.CompleteMainBox}
      data-e2e={
        pendingParentCompletion ? 'child-pending-complete' : 'child-complete'
      }
    >
      {getBody()}
      <div className={css.CompleteBody}>
        <p> {bodyText}</p>
        <div data-e2e="app-link">
          <p className={css.appStoreText}>{downloadAppText}</p>
          <AppDownloadLinks promoId="" partner="" />
        </div>
      </div>
    </div>
  );
};

export default ChildComplete;
